import { startTransition, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import { post } from '@rewardopl/utils/network';

import LoadingText from '../../loading_text';

import { maybeCurrentAccountState } from '../../../store';

import type { Account } from '@rewardopl/types';

type VerifyFormProps = {
  id: Account['_id'];
  token: NonNullable<Account['email_verification_token']>;
};

let didSend = false;

export default function VerifyForm({ id, token }: VerifyFormProps) {
  const navigate = useNavigate();
  const setCurrentAccount = useSetAtom(maybeCurrentAccountState);
  const emailAlreadyVerifiedString = useTranslation('Email has already been verified');
  const tokenIsInvalidString = useTranslation(
    'Email verification link is invalid. Check if you copied the link correctly',
  );
  const tokenIsExpiredString = useTranslation(
    'Email verification link has expired. Request email verification again',
  );
  const genericErrorString = useTranslation('Failed to verify email');

  const action = `/api/accounts/${id}/verify_email`;

  useEffect(() => {
    if (didSend) {
      return;
    }

    didSend = true;

    (async () => {
      const formData = new FormData();

      formData.append('token', token);

      try {
        const account = (await post(action, {
          body: formData,
        })) as Account;

        startTransition(() => {
          setCurrentAccount((prevAccount) => (prevAccount ? account : null));

          navigate('/login');
        });
      } catch (err) {
        const error = err;

        if (error instanceof Error && error.message === 'Account is already verified') {
          toast.success(emailAlreadyVerifiedString);

          navigate('/login');

          return;
        }

        const errorString = (() => {
          if (!(error instanceof Error)) {
            return genericErrorString;
          }

          switch (error.message) {
            case 'token is invalid':
              return tokenIsInvalidString;
            case 'token is expired':
              return tokenIsExpiredString;
            default:
              return genericErrorString;
          }
        })();

        toast.error(errorString);
      }

      didSend = false;
    })();
  }, [
    action,
    genericErrorString,
    navigate,
    setCurrentAccount,
    token,
    tokenIsExpiredString,
    tokenIsInvalidString,
    emailAlreadyVerifiedString,
  ]);

  return <LoadingText />;
}
